// src/modules/app-views/services/pages/Init/modals/ServicesParametersModal/tabs/UnitFormModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Flex, Space, Form, Input } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const UnitFormModal = ({ open, onCancel, onSave, initialData, loading, mousePosition }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();
  const inputRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!initialData;
  const title = isEditing ? translateX('edit_unit') : translateX('new_unit');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      if (initialData) {
        form.setFieldsValue({
          name: initialData.name || initialData.abbreviation || initialData.label || '',
          description: initialData.description || '',
        });
      } else {
        form.resetFields();
      }

      // Auto-focus on name field
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    }
  }, [open, initialData, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      onSave({
        name: (values.name || '').trim().toUpperCase(),
        description: (values.description || '').trim(),
      });
    } catch (error) {
      // Validation failed - form will show errors
      console.log('[UnitFormModal] Validation failed:', error);
    }
  }, [form, onSave]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  const handleAfterClose = useCallback(() => {
    form.resetFields();
  }, [form]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      afterClose={handleAfterClose}
      destroyOnHidden
      centered
      mousePosition={mousePosition}
      getContainer={document.body}
      width={400}
      footer={
        <Flex justify="end" style={{ paddingTop: 8 }}>
          <Space>
            <Button onClick={handleCancel} disabled={loading} icon={<CloseOutlined />}>
              {translateX('cancel')}
            </Button>
            <Button
              type="primary"
              loading={loading}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
            >
              {translateX('save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onKeyDown={handleKeyDown}
        initialValues={{ name: '', description: '' }}
      >
        <Form.Item
          name="name"
          label={translateX('name')}
          rules={[{ required: true, message: translateX('required_field') }]}
        >
          <Input
            ref={inputRef}
            maxLength={3}
            style={{ textTransform: 'uppercase', height: 40 }}
            placeholder="UN, KG, M..."
            disabled={loading}
          />
        </Form.Item>

        <Form.Item name="description" label={translateX('description')}>
          <Input
            maxLength={50}
            style={{ height: 40 }}
            placeholder={translateX('description_placeholder')}
            disabled={loading}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default UnitFormModal;
